import { useCallback, useContext, useEffect } from 'react';
import { AppErrorContext } from './AppErrorBoundary';
import useAnalyticsTracking from 'src/hooks/useAnalyticsTracking';
import { AnalyticsEventErrorProperties } from 'src/services/analytics';
import { AppErrorName } from './AppErrorMap';
import { UnionOmit } from 'src/types/unionOmit';

const appErrorToAnalyticsErrorMap: Record<
  AppErrorName,
  UnionOmit<AnalyticsEventErrorProperties, 'message'>
> = {
  somethingWentWrong: {
    category: 'runtime-error',
    name: 'application-offline',
  },
  failedFetchingFeatureFlags: {
    category: 'api-error',
    name: 'fetching-feature-flags-failed',
  },
  signupRequestFailed: {
    category: 'api-error',
    name: 'signup-failed',
  },
  failedMembershipBackendValidation: {
    category: 'api-error',
    name: 'membership-backend-validation-failed',
  },
  storeIdOrCountryMissing: {
    category: 'configuration-error',
    name: 'missing-country-or-store-id',
  },
  applicationOffline: {
    category: 'runtime-error',
    name: 'application-offline',
  },
};

const useAppErrorTracker = (): void => {
  const { errors } = useContext(AppErrorContext);
  const trackEvent = useAnalyticsTracking();

  const trackErrorEvent = useCallback(
    (properties: AnalyticsEventErrorProperties) =>
      trackEvent({
        name: 'error',
        properties,
      }),
    [trackEvent]
  );

  useEffect(() => {
    if (errors && errors.length > 0) {
      errors.forEach((error) =>
        trackErrorEvent({ ...appErrorToAnalyticsErrorMap[error], message: '' })
      );
    }
  }, [errors, trackErrorEvent]);
};

export default useAppErrorTracker;
